import React from "react";
import { Bell, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useWatched, addWatch, removeWatch, ensureNotifyPermission } from "@/lib/voteWatch";
import { cn } from "@/lib/format";

// Watch toggle for an active governance action. Requests browser-notification
// permission on first watch (a user gesture) and stores the live `closeTs` so
// the alert runner can fire before voting closes. `item` must carry
// { txHash, certIndex, title, type, status, closeTs }.
export const WatchButton = ({ item, tid = "vote-watch-toggle", compact = false, disabled = false, iconOnly = false }) => {
  const watched = useWatched(item.txHash, item.certIndex);
  const blocked = disabled && !watched;

  const onClick = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (blocked) return;
    if (watched) {
      removeWatch(item.txHash, item.certIndex);
      toast("Stopped watching this action");
      return;
    }
    const perm = await ensureNotifyPermission();
    addWatch(item);
    if (perm === "granted") {
      toast.success("Watching this action", { description: "You'll get a browser alert before voting closes." });
    } else if (perm === "denied") {
      toast.success("Added to your watch list", { description: "Allow notifications in your browser to also get an alert before it closes." });
    } else if (perm === "unsupported") {
      toast.success("Added to your watch list", { description: "This browser can't show alerts, but it stays on your list with a live countdown." });
    } else {
      toast.success("Added to your watch list");
    }
  };

  if (iconOnly) {
    return (
      <button
        type="button"
        data-testid={tid}
        aria-pressed={watched}
        aria-label={watched ? "Stop watching" : "Watch for a closing alert"}
        title={watched ? "Watching — you'll be alerted before it closes" : "Watch for a closing alert"}
        disabled={blocked}
        onClick={onClick}
        className={cn(
          "grid place-items-center h-7 w-7 rounded-full border transition-colors",
          blocked && "opacity-40 cursor-not-allowed",
          watched
            ? "border-[#22d3ee]/50 bg-[#22d3ee]/10 text-[#22d3ee]"
            : "border-primary/25 bg-primary/[0.04] text-muted-foreground hover:text-foreground hover:border-[#22d3ee]/50",
        )}
      >
        {watched ? <BellRinging size={13} weight="fill" /> : <Bell size={13} />}
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid={tid}
      aria-pressed={watched}
      disabled={blocked}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border transition-colors mono uppercase tracking-[0.15em]",
        compact ? "px-2.5 py-1 text-[10px]" : "px-3 py-1 text-[11px]",
        blocked && "opacity-40 cursor-not-allowed",
        watched
          ? "border-[#22d3ee]/50 bg-[#22d3ee]/10 text-[#22d3ee]"
          : "border-primary/25 bg-primary/[0.04] text-muted-foreground hover:text-foreground hover:border-[#22d3ee]/50",
      )}
    >
      {watched ? <BellRinging size={13} weight="fill" /> : <Bell size={13} />}
      {watched ? "Watching" : "Watch"}
    </button>
  );
};
